import { useEffect, useMemo, useState } from 'preact/hooks';
import { todayIso, type LeaderboardApi } from '../../net/leaderboard';
import { safeLocalKv } from '../flow';
import { cacheDailySeed, cachedDailySeed, localDailySeed, type DailyChoice } from './dailySeed';

/** Délai d'attente de la seed serveur avant de passer sur la seed locale. */
export const DAILY_SEED_TIMEOUT_MS = 4000;

interface DailySeed { seed: number | null; day: string; source: DailyChoice['source'] }

/**
 * Seed du jour : celle du serveur (lue d'abord dans le cache `rogfus.dailySeed.<jour>`, sinon demandée puis mise en cache ;
 * `null` le temps de la requête), à défaut (pas de client, appel en échec ou trop lent) la seed locale de repli.
 */
export function useDailySeed(api: LeaderboardApi | null): DailySeed {
  const kv = useMemo(() => safeLocalKv(), []);
  const initial = (): DailySeed => {
    const day = todayIso();
    const cached = cachedDailySeed(kv, day);
    if (cached !== null) return { seed: cached, day, source: 'server' };
    return api ? { seed: null, day, source: 'server' } : { seed: localDailySeed(day), day, source: 'local' };
  };
  const [state, setState] = useState<DailySeed>(initial);
  useEffect(() => {
    const first = initial();
    setState(first);
    if (first.seed !== null || !api) return;
    const day = first.day;
    let live = true;
    const fallback = () => { if (live) { live = false; setState({ seed: localDailySeed(day), day, source: 'local' }); } };
    // Sans réponse du serveur au bout de DAILY_SEED_TIMEOUT_MS, repli sur la seed locale ; une réponse tardive est ignorée.
    const timer = setTimeout(fallback, DAILY_SEED_TIMEOUT_MS);
    api.dailySeed(day).then(
      (seed) => { if (live) { live = false; clearTimeout(timer); cacheDailySeed(kv, day, seed); setState({ seed, day, source: 'server' }); } },
      () => { clearTimeout(timer); fallback(); },
    );
    return () => { live = false; clearTimeout(timer); };
  }, [api]);
  return state;
}

/** Bouton « Songe du jour » : lance la run avec la seed du jour (désactivé le temps de la récupérer). */
export function DailyButton({ api, className, onDaily }: { api: LeaderboardApi | null; className: string; onDaily: (daily: DailyChoice) => void }) {
  const { seed, day, source } = useDailySeed(api);
  const label = seed === null ? 'Songe du jour' : source === 'local' ? 'Songe du jour (hors ligne)' : `Songe du jour (seed ${seed})`;
  return <button type="button" class={className} disabled={seed === null} onClick={() => { if (seed !== null) onDaily({ seed, day, source }); }}>{label}</button>;
}
